import { useCallback, useMemo } from 'react'
import { listGroupLabel } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { FadingScrollable } from '@renderer/chat/components/elements/FadingScrollable'
import {
  MentionableRow,
  MentionSectionHeading,
  mentionsPopupClassName,
  SKILLS_PAGE_LINK,
  toSkillMention,
} from '@renderer/chat/components/chat/input/mentions'
import type { MentionableItem, Skill } from '@renderer/chat/types'

interface SidebarSkillPopupProps {
  skills: Array<Skill>
  highlightedIndex: number
  onPick: (skill: Skill) => void
}

/**
 * The bar's `/` list, drawn with the composer's own heading and mention rows so the two menus look alike. View all
 * opens every skill in Settings.
 */
export function SidebarSkillPopup({ skills, highlightedIndex, onPick }: SidebarSkillPopupProps) {
  const mentions = useMemo(() => skills.map(toSkillMention), [skills])

  const pickMention = useCallback(
    (mention: MentionableItem) => {
      const skill = skills.find((candidate) => candidate.id === mention.value)
      if (skill) onPick(skill)
    },
    [skills, onPick],
  )

  return (
    <div className={twMerge(mentionsPopupClassName, 'flex max-h-80 flex-col overflow-hidden')}>
      <FadingScrollable className="flex h-full w-full flex-col gap-px overflow-y-auto p-1">
        <div className={twMerge(listGroupLabel, 'flex justify-between')}>
          <MentionSectionHeading
            section={{ label: 'Skills', items: mentions, linkToPage: SKILLS_PAGE_LINK, offset: 0 }}
          />
        </div>
        {mentions.map((mention, index) => (
          <MentionableRow
            key={mention.value}
            mention={mention}
            isSelected={index === highlightedIndex}
            onSelect={pickMention}
          />
        ))}
      </FadingScrollable>
    </div>
  )
}
